import React, { useState, useEffect } from 'react';
import firebaseDataService from '../../../services/FirebaseDataService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Avatar,
  Grid,
  Paper,
  Divider,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import StoreIcon from '@mui/icons-material/Store';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { useAuth } from '../../../contexts/AuthContext';
// ✅ Firebase import'ları kaldırıldı - AuthContext cache kullanılıyor

interface PersonnelProfileModalProps {
  open: boolean;
  onClose: () => void;
}

// Stillendirilmiş bileşenler
const ProfileHeader = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.primary.dark,
  padding: theme.spacing(3, 2),
  borderRadius: `0 0 ${theme.spacing(3)}px ${theme.spacing(3)}px`,
  color: 'white',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  marginBottom: theme.spacing(2)
}));

const AvatarWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5),
  borderRadius: '50%',
  border: `3px solid ${theme.palette.common.white}`,
  display: 'inline-flex'
}));

const ProfileBadge = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: theme.spacing(2.5),
  padding: theme.spacing(0.5, 2),
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: theme.spacing(2)
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  transition: 'box-shadow 0.3s ease'
}));

const IconContainer = styled(Box)(({ theme }) => ({
  backgroundColor: `${theme.palette.primary.dark}15`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const PersonnelProfileModal: React.FC<PersonnelProfileModalProps> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [branchName, setBranchName] = useState<string | null>(null);
  
  // ✅ Tüm kullanıcı bilgileri AuthContext cache'inden geliyor - Firebase sorgusu YOK!
  const { currentUser, userDetails, getBranchName } = useAuth();

  useEffect(() => {
    if (open && userDetails) {
      loadUserData();
    }
  }, [open, userDetails]);

  const loadUserData = async () => {
    // ✅ Kullanıcı bilgileri AuthContext cache'inden geliyor
    if (!userDetails) {
      setError('Kullanıcı bilgileri bulunamadı');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // ✅ AuthContext'ten gelen userDetails'i kullan - Firebase sorgusu YOK!
      const authUserData = {
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        email: userDetails.email,
        phone: (userDetails as any).phone || '',
        employeeId: (userDetails as any).employeeId || userDetails.id,
        id: userDetails.id,
        companyId: userDetails.companyId,
        branchesId: userDetails.branchesId
      };
      
      setUserData(authUserData);

      // ✅ Şirket adı da AuthContext'ten geliyor
      if (userDetails.companyName) {
        setCompanyName(userDetails.companyName);
      }

      // ✅ Şube bilgisi AuthContext cache'inden
      if (userDetails.branchesId) {
        const branchNameFromContext = getBranchName(userDetails.branchesId);
        setBranchName(branchNameFromContext !== 'Şubeye Ait Değil' ? branchNameFromContext : null);
      }

      setLoading(false);
    } catch (error) {
      console.error('Profil bilgileri yüklenirken hata:', error);
      setError('Profil bilgileri yüklenirken bir hata oluştu');
      setLoading(false);
    }
  };

  // ✅ loadCompanyInfo artık gereksiz - AuthContext cache kullanılıyor

  const handleClose = () => {
    onClose();
    // Modal kapandığında state'i sıfırla
    setTimeout(() => {
      setUserData(null);
      setCompanyName(null);
      setBranchName(null);
      setError(null);
    }, 300);
  };

  const getInitials = () => {
    if (!userData) return '';
    const firstName = userData.firstName || '';
    const lastName = userData.lastName || '';
    return `${firstName.charAt(0) || ''}${lastName.charAt(0) || ''}`.toUpperCase();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          minHeight: '50vh',
          width: '360px',
          maxWidth: '95vw'
        }
      }}
    >
      <DialogTitle sx={{ 
        p: 1.5, 
        backgroundColor: 'primary.dark', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="subtitle1" component="div">
          Profil Bilgileri
        </Typography>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={handleClose} 
          aria-label="close"
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress size={30} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Box>
        ) : userData ? (
          <>
            <ProfileHeader>
              <AvatarWrapper>
                <Avatar
                  sx={{ 
                    width: 70, 
                    height: 70, 
                    backgroundColor: 'white', 
                    color: 'primary.dark',
                    fontSize: '1.8rem',
                    fontWeight: 'bold'
                  }}
                >
                  {getInitials()}
                </Avatar>
              </AvatarWrapper>
              <Typography variant="h6" component="div" sx={{ mt: 1.5, fontWeight: 'bold' }}>
                {userData.firstName} {userData.lastName}
              </Typography>
              <ProfileBadge>
                <Typography variant="caption">
                  Personel
                </Typography>
              </ProfileBadge>
            </ProfileHeader>

            <Box sx={{ p: 2 }}>
              <InfoCard>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item>
                    <IconContainer>
                      <PersonIcon color="primary" fontSize="small" />
                    </IconContainer>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="caption" color="text.secondary">
                      Ad Soyad
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {userData.firstName} {userData.lastName}
                    </Typography>
                  </Grid>
                </Grid>
              </InfoCard>

              <InfoCard>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item>
                    <IconContainer>
                      <BadgeIcon color="primary" fontSize="small" />
                    </IconContainer>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="caption" color="text.secondary">
                      Personel ID
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {userData.employeeId || userData.id || 'Belirtilmemiş'}
                    </Typography>
                  </Grid>
                </Grid>
              </InfoCard>

              <InfoCard>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item>
                    <IconContainer>
                      <EmailIcon color="primary" fontSize="small" />
                    </IconContainer>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="caption" color="text.secondary">
                      E-posta
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {userData.email || 'Belirtilmemiş'}
                    </Typography>
                  </Grid>
                </Grid>
              </InfoCard>

              <InfoCard>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item>
                    <IconContainer>
                      <PhoneIcon color="primary" fontSize="small" />
                    </IconContainer>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="caption" color="text.secondary">
                      Telefon
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {userData.phone || 'Belirtilmemiş'}
                    </Typography>
                  </Grid>
                </Grid>
              </InfoCard>

              {companyName && (
                <InfoCard>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item>
                      <IconContainer>
                        <BusinessIcon color="primary" fontSize="small" />
                      </IconContainer>
                    </Grid>
                    <Grid item xs>
                      <Typography variant="caption" color="text.secondary">
                        Şirket
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {companyName}
                      </Typography>
                    </Grid>
                  </Grid>
                </InfoCard>
              )}

              {branchName && (
                <InfoCard>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item>
                      <IconContainer>
                        <StoreIcon color="primary" fontSize="small" />
                      </IconContainer>
                    </Grid>
                    <Grid item xs>
                      <Typography variant="caption" color="text.secondary">
                        Şube
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {branchName}
                      </Typography>
                    </Grid>
                  </Grid>
                </InfoCard>
              )}

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button 
                  onClick={handleClose} 
                  variant="outlined" 
                  size="small"
                >
                  Kapat
                </Button>
              </Box>
            </Box>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default PersonnelProfileModal; 